import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, Disc3, Headphones, Heart, Library, MapPin, Music, Play, Sparkles, UserRoundPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { usePlayer } from '../context/PlayerContext';
import { API_BASE as API } from '../lib/apiBase';


function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

export default function LibraryPage() {
  const { token, user } = useAuth();
  const { lang } = useLang();
  const { playSong } = usePlayer();
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API}/api/library/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        setLibrary(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  if (!user) {
    return (
      <div data-testid="library-page" className="min-h-screen pt-14 pb-32 flex flex-col items-center justify-center px-4 text-center">
        <Library size={34} className="text-pulse-primary mb-4" />
        <h1 className="font-heading font-bold text-2xl mb-2">{lang === 'es' ? 'Tu colección empieza al iniciar sesión' : 'Your collection starts when you log in'}</h1>
        <p className="text-sm text-pulse-muted max-w-md">{lang === 'es' ? 'Guarda artistas, sigue tu escucha y vuelve a lo que realmente te gusta.' : 'Save artists, track your listening and return to what you really like.'}</p>
        <Link to="/login" className="mt-6 rounded-full bg-pulse-primary px-6 py-3 text-sm font-bold text-black">{lang === 'es' ? 'Entrar' : 'Log in'}</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="library-page" className="min-h-screen pt-14 pb-32 px-4 md:px-12 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {[1, 2, 3].map((item) => <div key={item} className="h-32 rounded-[28px] shimmer-loading" />)}
        </div>
      </div>
    );
  }

  const data = library || { stats: {}, favorites: [], following: [], recent_songs: [], new_releases: [], playlists: [], recommended_events: [], taste_profile: { top_genres: [], top_cities: [] } };

  return (
    <div data-testid="library-page" className="min-h-screen pt-14 pb-32 px-4 md:px-12 py-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        <section className="rounded-[34px] border border-pulse-border bg-[radial-gradient(circle_at_top_left,rgba(0,212,255,0.12),transparent_30%),linear-gradient(180deg,rgba(20,20,24,0.96),rgba(10,10,15,1))] p-6 md:p-10 overflow-hidden relative">
          <div className="absolute right-0 top-0 w-64 h-64 rounded-full bg-pulse-primary/10 blur-3xl" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-pulse-muted">
                <Disc3 size={12} className="text-pulse-primary" /> {lang === 'es' ? 'Tu biblioteca musical' : 'Your music library'}
              </span>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-bold tracking-tight">{lang === 'es' ? `Tu colección, ${data.user_name || user.name}` : `${data.user_name || user.name}, your collection`}</h1>
              <p className="mt-4 text-sm md:text-base text-pulse-muted max-w-2xl leading-relaxed">
                {lang === 'es'
                  ? 'Aquí vive tu relación con la plataforma: artistas guardados, escucha reciente, playlists y eventos alineados con tu gusto.'
                  : 'This is where your relationship with the platform lives: saved artists, recent listening, playlists and events aligned with your taste.'}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 min-w-full lg:min-w-[430px]">
              {[
                { label: lang === 'es' ? 'Guardados' : 'Saved', value: data.stats.favorites_count, icon: Heart },
                { label: lang === 'es' ? 'Artistas fan' : 'Fan artists', value: data.stats.following_count, icon: UserRoundPlus },
                { label: lang === 'es' ? 'Escucha reciente' : 'Recent listening', value: data.stats.recent_listens_count, icon: Headphones },
                { label: 'Playlists', value: data.stats.playlists_count, icon: Music },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/10 bg-black/25 p-4">
                  <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center mb-3">
                    <item.icon size={16} className="text-pulse-primary" />
                  </div>
                  <p className="font-heading font-bold text-2xl">{item.value || 0}</p>
                  <p className="text-xs text-pulse-muted mt-1">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-4 lg:grid-cols-[1.2fr,0.8fr]">
          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Radar musical' : 'Music radar'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Tu perfil de gusto ahora mismo' : 'Your taste profile right now'}</h2>
              </div>
              <Sparkles size={18} className="text-pulse-primary" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-pulse-border bg-pulse-elevated p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-pulse-muted mb-3">{lang === 'es' ? 'Géneros que te mueven' : 'Genres moving you'}</p>
                <div className="flex flex-wrap gap-2">
                  {(data.taste_profile.top_genres || []).length > 0 ? data.taste_profile.top_genres.map((genre) => (
                    <span key={genre} className="rounded-full border border-pulse-border px-3 py-1 text-xs bg-pulse-surface">{genre}</span>
                  )) : <span className="text-sm text-pulse-muted">{lang === 'es' ? 'Empieza a escuchar para perfilar esta zona.' : 'Start listening to shape this area.'}</span>}
                </div>
              </div>
              <div className="rounded-2xl border border-pulse-border bg-pulse-elevated p-4">
                <p className="text-[11px] uppercase tracking-[0.18em] text-pulse-muted mb-3">{lang === 'es' ? 'Ciudades con más afinidad' : 'Cities with strongest affinity'}</p>
                <div className="flex flex-wrap gap-2">
                  {(data.taste_profile.top_cities || []).length > 0 ? data.taste_profile.top_cities.map((city) => (
                    <span key={city} className="rounded-full border border-pulse-border px-3 py-1 text-xs bg-pulse-surface inline-flex items-center gap-1"><MapPin size={10} /> {city}</span>
                  )) : <span className="text-sm text-pulse-muted">{lang === 'es' ? 'Cuando guardes artistas, verás tu mapa natural.' : 'Once you save artists, your natural map will appear here.'}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Acceso rápido' : 'Quick access'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Mueve tu biblioteca sin perder tiempo' : 'Move through your library without friction'}</h2>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { to: '/playlists', label: lang === 'es' ? 'Abrir mis playlists' : 'Open my playlists' },
                { to: '/events', label: lang === 'es' ? 'Ver eventos relacionados' : 'See related events' },
                { to: '/profile', label: lang === 'es' ? 'Ajustes y cuenta' : 'Account and settings' },
              ].map((item) => (
                <Link key={item.to} to={item.to} className="flex items-center justify-between rounded-2xl border border-pulse-border bg-pulse-elevated p-4 text-sm hover:border-white/20 transition-colors duration-300">
                  <span>{item.label}</span>
                  <ChevronRight size={16} className="text-pulse-primary" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-8 rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
          {(data.new_releases || []).length > 0 && (
            <div className="mb-8">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Alertas de lanzamiento' : 'Release alerts'}</p>
                  <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Novedades de artistas de los que eres fan' : 'New drops from artists you are a fan of'}</h2>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {data.new_releases.slice(0, 6).map((song, idx) => {
                  const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                  return (
                    <button key={song.id} data-testid={`library-new-release-${song.id}`} onClick={() => playSong(song, data.new_releases, idx)} className="rounded-2xl border border-pulse-border bg-pulse-elevated p-4 flex items-center gap-4 text-left hover:border-white/20 transition-colors duration-300">
                      <img src={song.cover} alt={title} className="w-14 h-14 rounded-2xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{title}</p>
                        <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
                        <p className="text-[11px] text-pulse-primary mt-1">{lang === 'es' ? 'Nuevo lanzamiento' : 'New release'}</p>
                      </div>
                      <Play size={16} className="text-pulse-primary shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Escuchado recientemente' : 'Recently played'}</p>
              <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Vuelve a lo que ya te estaba sonando bien' : 'Return to what was already sounding right'}</h2>
            </div>
          </div>

          {(data.recent_songs || []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-pulse-border p-8 text-center text-pulse-muted text-sm">
              {lang === 'es' ? 'Todavía no hay escucha reciente guardada. Reproduce unas canciones y esta zona cobrará vida.' : 'No recent listening yet. Play a few tracks and this section will come alive.'}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {data.recent_songs.map((song, idx) => {
                const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                return (
                  <button key={song.id} data-testid={`library-recent-song-${song.id}`} onClick={() => playSong(song, data.recent_songs, idx)} className="rounded-2xl border border-pulse-border bg-pulse-elevated p-4 flex items-center gap-4 text-left hover:border-white/20 transition-colors duration-300">
                    <img src={song.cover} alt={title} className="w-14 h-14 rounded-2xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{title}</p>
                      <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
                      <p className="text-[11px] text-pulse-muted mt-1 inline-flex items-center gap-1"><Headphones size={10} /> {formatNum(song.plays)} plays</p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-pulse-primary"><Play size={16} /></div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-8 grid gap-6 xl:grid-cols-2">
          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Artistas guardados' : 'Saved artists'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Tu colección emocional' : 'Your emotional collection'}</h2>
              </div>
            </div>
            {(data.favorites || []).length === 0 ? (
              <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Todavía no has guardado artistas. Empieza desde la home o desde cualquier perfil.' : 'You have not saved artists yet. Start from the home feed or any artist profile.'}</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {data.favorites.map((artist) => (
                  <Link key={artist.id} to={`/artist/${artist.id}`} className="rounded-2xl border border-pulse-border bg-pulse-elevated p-4 hover:border-white/20 transition-colors duration-300">
                    <div className="flex items-center gap-3">
                      <img src={artist.photo} alt={artist.name} className="w-14 h-14 rounded-2xl object-cover" />
                      <div className="min-w-0">
                        <p className="font-heading font-semibold truncate">{artist.name}</p>
                        <p className="text-xs text-pulse-muted truncate">{artist.city || 'España'} · {(artist.genre || []).slice(0, 2).join(', ')}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between text-xs text-pulse-muted">
                      <span>{formatNum(artist.plays)} plays</span>
                      <span className="text-white">{lang === 'es' ? 'Ver artista' : 'View artist'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Artistas que sigues' : 'Artists you follow'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'La escena que quieres seguir de cerca' : 'The scene you want to follow closely'}</h2>
              </div>
            </div>
            {(data.following || []).length === 0 ? (
              <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Cuando sigas artistas, esta zona te ayudará a no perderles la pista.' : 'Once you follow artists, this area will help you keep track of them.'}</p>
            ) : (
              <div className="space-y-3">
                {data.following.map((artist) => (
                  <Link key={artist.id} to={`/artist/${artist.id}`} className="flex items-center justify-between gap-4 rounded-2xl border border-pulse-border bg-pulse-elevated p-4 hover:border-white/20 transition-colors duration-300">
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={artist.photo} alt={artist.name} className="w-12 h-12 rounded-2xl object-cover" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{artist.name}</p>
                        <p className="text-xs text-pulse-muted truncate">{artist.city || 'España'} · {formatNum(artist.followers)} {lang === 'es' ? 'fans' : 'fans'}</p>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-pulse-primary shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="mt-8 grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">Playlists</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Tus carpetas musicales' : 'Your music folders'}</h2>
              </div>
              <Link to="/playlists" className="text-sm text-pulse-primary">{lang === 'es' ? 'Abrir playlists' : 'Open playlists'}</Link>
            </div>
            {(data.playlists || []).length === 0 ? (
              <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Aún no has creado playlists. Puedes empezar con una carpeta para descubrir, otra para favoritos y otra para eventos.' : 'You have not created playlists yet. Start with one for discovery, one for favorites and one for events.'}</p>
            ) : (
              <div className="space-y-3">
                {data.playlists.map((playlist) => (
                  <Link key={playlist.id} to="/playlists" className="flex items-center justify-between gap-3 rounded-2xl border border-pulse-border bg-pulse-elevated p-4 hover:border-white/20 transition-colors duration-300">
                    <div>
                      <p className="font-medium">{playlist.name}</p>
                      <p className="text-xs text-pulse-muted">{playlist.songs?.length || 0} {lang === 'es' ? 'canciones' : 'songs'}</p>
                    </div>
                    <ChevronRight size={16} className="text-pulse-primary" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Eventos para ti' : 'Events for you'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Escena recomendada según tu gusto' : 'Scene recommendations based on your taste'}</h2>
              </div>
              <Link to="/events" className="text-sm text-pulse-primary">{lang === 'es' ? 'Mapa completo' : 'Full map'}</Link>
            </div>
            <div className="space-y-3">
              {(data.recommended_events || []).slice(0, 5).map((event) => {
                const title = lang === 'en' ? (event.title_en || event.title_es) : event.title_es;
                return (
                  <Link key={event.id} to="/events" className="flex items-center gap-4 rounded-2xl border border-pulse-border bg-pulse-elevated p-4 hover:border-white/20 transition-colors duration-300">
                    <img src={event.image} alt={title} className="w-16 h-16 rounded-2xl object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{title}</p>
                      <p className="text-xs text-pulse-muted truncate">{event.location?.name || ''} · {new Date(event.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' })}</p>
                    </div>
                    <span className="text-sm text-pulse-primary shrink-0 inline-flex items-center gap-1"><Calendar size={14} /> {event.price}€</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}